import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { accessRequestApi } from "../../services/accessRequestApi";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { Spinner } from "../../components/common/Spinner";
import { ROUTES } from "../../routes";
import styles from "./LoginScreen.module.css";

const DURATIONS = [
  { label: "1 hour", minutes: 60 },
  { label: "4 hours", minutes: 240 },
  { label: "1 day", minutes: 1440 },
];

const STATUS_TONE = { Pending: "warning", Approved: "success", Denied: "danger" };

/**
 * JIT access request form -- wired to the real /management/access-requests
 * routes (see accessRequestApi.js). Rendered both when a signed-in admin
 * navigates here directly (ROUTES.ACCESS_REQUEST) and, more commonly, when
 * apiClient.js flips AuthContext's `jitRequired` after any request 403s with
 * jit_access_required -- App.jsx's AuthedApp shows this page in place of the
 * normal routes whenever that's set.
 *
 * On mount this checks the requester's own latest request (GET
 * /management/access-requests/mine) so returning here reflects reality --
 * Pending/Approved/Denied -- rather than only ever showing the blank form.
 */
export function AccessRequestPage() {
  const { logout, setJitRequired } = useAuth();
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [minutes, setMinutes] = useState(240);
  // undefined = still checking status; null = no request on file yet (show the form)
  const [latestRequest, setLatestRequest] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshStatus() {
    try {
      const r = await accessRequestApi.myStatus();
      setLatestRequest(r.latest_request);
      setError(null);
    } catch (err) {
      setError(err.apiMessage || "Could not check request status");
      setLatestRequest((prev) => (prev === undefined ? null : prev));
    }
  }

  async function submit() {
    if (!reason.trim()) return setError("Enter a reason for requesting access");
    setBusy(true);
    setError(null);
    try {
      const r = await accessRequestApi.create(reason.trim(), minutes);
      setLatestRequest({ request_id: r.request_id, status: r.status, requested_date: new Date().toISOString() });
    } catch (err) {
      setError(err.apiMessage || "Could not submit request");
    } finally {
      setBusy(false);
    }
  }

  function continueToApp() {
    setJitRequired?.(false);
    navigate(ROUTES.HOME, { replace: true });
  }

  if (latestRequest === undefined) return <Spinner />;

  return (
    <div className={styles.wrapper}>
      <Card title="Request Admin Access" style={{ maxWidth: 480, width: "100%" }}>
        {latestRequest ? (
          <>
            <Badge tone={STATUS_TONE[latestRequest.status] || "neutral"}>{latestRequest.status}</Badge>

            {latestRequest.status === "Pending" && (
              <p style={{ marginTop: "var(--space-3)" }}>
                Your request has been submitted. A second admin must approve it before you can sign in here -- refresh
                below to check for a decision.
              </p>
            )}
            {latestRequest.status === "Approved" && (
              <p style={{ marginTop: "var(--space-3)" }}>
                Your access has been approved
                {latestRequest.expires_at ? ` until ${new Date(latestRequest.expires_at).toLocaleString()}` : ""}.
                Continue to the console below.
              </p>
            )}
            {latestRequest.status === "Denied" && (
              <p style={{ marginTop: "var(--space-3)" }}>
                Your request was denied{latestRequest.denial_reason ? `: ${latestRequest.denial_reason}` : "."} You can
                submit a new request below.
              </p>
            )}

            {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

            <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-4)", flexWrap: "wrap" }}>
              {latestRequest.status === "Approved" && <Button onClick={continueToApp}>Continue</Button>}
              {latestRequest.status !== "Approved" && (
                <Button variant="secondary" onClick={refreshStatus}>Refresh Status</Button>
              )}
              {latestRequest.status === "Denied" && (
                <Button onClick={() => setLatestRequest(null)}>Submit New Request</Button>
              )}
              <Button variant="secondary" onClick={logout}>Sign Out</Button>
            </div>
          </>
        ) : (
          <>
            <label style={{ display: "block", marginBottom: "var(--space-3)" }}>
              Reason for access
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="touch-target"
                style={{ width: "100%", display: "block", marginTop: 4 }}
                placeholder="e.g. Investigating a support escalation for account #4821"
              />
            </label>
            <label style={{ display: "block", marginBottom: "var(--space-4)" }}>
              Access duration
              <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} className="touch-target" style={{ display: "block", marginTop: 4 }}>
                {DURATIONS.map((d) => <option key={d.minutes} value={d.minutes}>{d.label}</option>)}
              </select>
            </label>
            {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              <Button onClick={submit} disabled={busy}>{busy ? "Submitting…" : "Submit Request"}</Button>
              <Button variant="secondary" onClick={logout}>Cancel</Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
